import { useState, useMemo, useContext } from 'react';
import { useQuery } from 'react-query';
import { Box, Button } from '@mui/material';
import {
  Page,
  PageTitleWithButton,
  Table,
  Pagination,
  SearchSection
} from '../../../../components';
import { useSnackbar } from 'notistack';
import { IResponse, IOptionCategory, ISortDirection } from '../../../../models';
import { deleteOptionCategory, getOptionCategories } from '../../../../fetch';
import { Add, Delete, Edit } from '@mui/icons-material';
import { OptionCategoryFilters } from './option-category-filters';
import { AddEditOptionCategory } from './option-add-edit-modal';
import { SalesContext, update } from '../../../../context/sales';
import { SALES_PORTAL_PER_PAGE_OPTIONS, getEnvironment } from '../../../../helpers';

const PAGE_NAME = 'option-category-descriptions';
export const OptionCategoryDescriptions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );
  const [selectedSort, setSelectedSort] = useState<string>('CategoryName');
  const [sortDirection, setSortDirection] = useState<{
    CategoryName?: ISortDirection;
    Description?: ISortDirection;
  }>({
    CategoryName: 'Asc'
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [currentCategory, setCurrentCategory] = useState<IOptionCategory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { isLoading, data, refetch } = useQuery<IResponse<IOptionCategory[]>, Error>(
    ['getOptionCategories', page, perPage, selectedSort, sortDirection, searchedValue],
    () =>
      getOptionCategories({
        page: page + 1,
        perPage,
        search: searchedValue,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const optionCategories = useMemo(() => data?.records || [], [data]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Category Name',
        accessor: 'categoryName',
        overrideWidth: 300,
        isServerSorted: selectedSort === 'CategoryName',
        isServerSortedDesc: sortDirection.CategoryName === 'Desc',
        handleClickColumn: () => handleClickColumn('CategoryName')
      },
      {
        Header: 'Description',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description')
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOptionCategory } };
        }) => {
          return (
            <Box display="flex">
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setIsModalOpen(true);
                  setCurrentCategory(original);
                }}
              >
                Edit
              </Button>
              <Button
                color="error"
                startIcon={<Delete />}
                onClick={async () => {
                  const result = window.confirm('Are you sure you want to delete this category?');
                  if (result) {
                    try {
                      await deleteOptionCategory(original.categoryName);
                      enqueueSnackbar('Category deleted!', {
                        variant: 'success'
                      });
                      refetch();
                    } catch (error) {
                      enqueueSnackbar(error?.Detail ?? 'Error deleting category', {
                        variant: 'success'
                      });
                    }
                  }
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionCategories, selectedSort, sortDirection]);
  return (
    <Page pageHasStickyHeaders title="Option Category Descriptions">
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Option Category Descriptions'
        }}
        button={
          <Button
            color="primary"
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            Add New Option Category Description
          </Button>
        }
      />
      <OptionCategoryFilters
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        isLoading={isLoading}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
        }}
      />
      {searchedValue && (
        <SearchSection
          searchedValue={searchedValue}
          handleClearSearch={() => {
            setSearchValue('');
            setSearchedValue('');
            setPage(0);
          }}
        />
      )}
      <Table
        stickyHeaders
        columns={columns}
        data={optionCategories}
        isLoading={isLoading}
        hidePagination
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
      <AddEditOptionCategory
        open={isModalOpen}
        currentCategory={currentCategory}
        onClose={() => {
          setCurrentCategory(null);
          setIsModalOpen(false);
        }}
        fetchData={() => refetch()}
      />
    </Page>
  );
};
